import { InitialUrlSearchParams, SiteSettings } from 'src/scripts/utils/environment';
import { getCookie, setCookie } from 'src/scripts/utils/cookies';

const API_GATEWAY_STAGE_HEADER = 'alo-apigateway-stage';
const API_GATEWAY_STAGES = ['staging'] as const;
const ALO_API_STAGE_PARAM_KEY = 'alo-api-stage';
const PRODUCT_SERVICE_STAGE_PARAM_KEY = 'product-service-stage';
const ALL_STAGE_PARAM_KEYS = [ALO_API_STAGE_PARAM_KEY, PRODUCT_SERVICE_STAGE_PARAM_KEY];

type ApiGatewayStageType = typeof API_GATEWAY_STAGES[number];
type ApiGatewayStageHeader = {
  [API_GATEWAY_STAGE_HEADER]?: ApiGatewayStageType;
};

const isValidApiGatewayStage = (value: string): value is ApiGatewayStageType => {
  return API_GATEWAY_STAGES.includes(value as ApiGatewayStageType);
};

const handleApiGatewayStageHeader = () => {
  if (!SiteSettings?.enableUrlParamApiGatewayStage) {
    return;
  }

  const urlParams = new URLSearchParams(InitialUrlSearchParams);
  ALL_STAGE_PARAM_KEYS.forEach((paramKey) => {
    const paramValue = urlParams.get(paramKey);

    if (paramValue !== null) {
      if (isValidApiGatewayStage(paramValue)) {
        // set cookie to keep alo-apigateway-stage for when users navigate
        setCookie(paramKey, paramValue, 1); // 1 day expiry
        console.log(`Using ${paramKey} from url param: ${paramValue}`);
        return;
      }
      // remove url param and cookie if invalid (for users to switch back to production)
      urlParams.delete(paramKey);
      const paramStr = urlParams.toString();
      window.history.replaceState({}, document.title, `${window.location.pathname}${paramStr ? `?${paramStr}` : ''}`);
      setCookie(paramKey, '', -1);
      return;
    }

    const stageCookies = getCookie(paramKey);
    if (stageCookies) {
      if (isValidApiGatewayStage(stageCookies)) {
        urlParams.set(paramKey, stageCookies);
        const paramStr = urlParams.toString();
        // add url param to let users know what stage they are on and for sharing links
        window.history.replaceState({}, document.title, `${window.location.pathname}${paramStr ? `?${paramStr}` : ''}`);
        console.log(`Using ${paramKey} from cookie: ${stageCookies}`);
        return;
      }
      setCookie(paramKey, '', -1);
    }
  });
};
handleApiGatewayStageHeader();

const getApiGatewayStageHeader = (paramKey: string): ApiGatewayStageHeader => {
  if (!SiteSettings?.enableUrlParamApiGatewayStage) {
    return {};
  }
  const urlParams = new URLSearchParams(window.location.search);
  const paramValue = urlParams.get(paramKey);
  if (paramValue && isValidApiGatewayStage(paramValue)) {
    return { [API_GATEWAY_STAGE_HEADER]: paramValue };
  }
  return {};
};

export const getAloApiStageHeader = (): ApiGatewayStageHeader => getApiGatewayStageHeader(ALO_API_STAGE_PARAM_KEY);

export const getProductServiceStageHeader = (): ApiGatewayStageHeader =>
  getApiGatewayStageHeader(PRODUCT_SERVICE_STAGE_PARAM_KEY);
